import type { GetDocumentByPathQuery } from '#src/graphql/generated';
import type { EditorJsEditorHandle } from './editor';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { EditDocumentProvider } from './edit-document-context';
import { parseEditorJs, stringifyEditorJs } from './editorjs-types';
import { ViewDocumentHeader } from './view-document-header';
import { ViewDocumentViewer } from './view-document-viewer';

import { useAppConfig } from '#src/providers/app-config-provider';
import { useCurrentUser } from '#src/providers/user-provider';
import { formatDocumentName } from '#src/utils/format-document-name';

export type DocumentByPathResult = GetDocumentByPathQuery['documentByPath'];

interface ViewDocumentProps {
  /** Document from parent query (Document or DocumentNotFound). */
  document: DocumentByPathResult;
  /** Canonical path (document path when found, else requested path from URL). */
  path: string;
  refetch: () => Promise<unknown>;
}

/** Path from DocumentResult (Document or DocumentNotFound). */
function getPathFromResult(
  result: DocumentByPathResult,
  fallback: string,
): string {
  return result?.__typename === 'Document'
    ? result.path
    : result?.__typename === 'DocumentNotFound'
      ? (result.requestedPath ?? fallback)
      : fallback;
}

/** Part of child path after parent path (for display). */
function ViewDocumentContent({ document, path, refetch }: ViewDocumentProps) {
  const navigate = useNavigate();
  const { config, recordRecentlyOpenedDocument } = useAppConfig();
  const user = useCurrentUser();

  const isNewDocument = document?.__typename === 'DocumentNotFound';
  const documentId =
    document?.__typename === 'Document' ? document.id : undefined;
  const displayPath = getPathFromResult(document, path);

  const [isCreateSubpageOpen, setIsCreateSubpageOpen] = useState(false);

  const childrenItems =
    (document?.children?.items as Array<{ id: string; path: string }> | null) ??
    [];
  const childrenHasMore = document?.children?.pagination?.hasMore ?? false;

  useEffect(() => {
    if (documentId) {
      recordRecentlyOpenedDocument(documentId, path);
    }
  }, [documentId, path, recordRecentlyOpenedDocument]);

  const editorRef = useRef<EditorJsEditorHandle | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const documentValue =
    document?.__typename === 'Document' ? document.value : null;

  const [editorInitialData] = useState(() => {
    const parsed = documentValue
      ? (parseEditorJs(documentValue) ?? null)
      : null;
    if (parsed?.blocks?.length) return parsed;
    const segments = formatDocumentName(displayPath);
    const title =
      segments.length > 0
        ? segments[segments.length - 1]
        : displayPath || 'Untitled';
    return {
      time: Date.now(),
      blocks: [{ type: 'header', data: { text: title, level: 1 } }],
      version: '2.0.0',
    };
  });

  const [lastSavedValue, setLastSavedValue] = useState(
    stringifyEditorJs(editorInitialData),
  );

  // Sync lastSavedValue when document loads or changes externally (e.g. initial load)
  useEffect(() => {
    setLastSavedValue(stringifyEditorJs(editorInitialData));
  }, [editorInitialData]);

  const onNavigateToDocument = useCallback(
    (docPath: string) => {
      navigate(`/documents/${docPath}`);
    },
    [navigate],
  );

  const openCreateSubpagePrompt = useCallback(() => {
    setIsCreateSubpageOpen(true);
  }, []);

  // Note: handleFilePaste removed; complex format no longer supports raw paste → markdown mapping here.

  // Track changes in the editor
  useEffect(() => {
    const checkForChanges = () => {
      const currentValue = stringifyEditorJs(
        editorRef.current?.getValue() ?? null,
      );
      setHasChanges(currentValue !== lastSavedValue);
    };

    // Check periodically for changes
    const interval = setInterval(checkForChanges, 500);
    return () => clearInterval(interval);
  }, [lastSavedValue]);

  return (
    <EditDocumentProvider editorRef={editorRef}>
      <ViewDocumentHeader
        document={document}
        path={path}
        displayPath={displayPath}
        documentId={documentId}
        isNewDocument={isNewDocument}
        childrenItems={childrenItems}
        childrenHasMore={childrenHasMore}
        onNavigateToHistory={() =>
          navigate(`/documents/${displayPath}/history`)
        }
        hasChanges={hasChanges}
        getCurrentValue={() =>
          stringifyEditorJs(editorRef.current?.getValue() ?? null)
        }
        refetch={refetch}
        onAfterSave={(savedValueString: string) => {
          const savedData = parseEditorJs(savedValueString) ?? null;
          editorRef.current?.resetContent(savedData);
          setLastSavedValue(savedValueString);
          setHasChanges(false);
        }}
        isCreateSubpageOpen={isCreateSubpageOpen}
        onCreateSubpageOpenChange={setIsCreateSubpageOpen}
      />

      <ViewDocumentViewer
        editorRef={editorRef}
        documentId={documentId}
        isNewDocument={isNewDocument}
        path={path}
        displayPath={displayPath}
        value={editorInitialData}
        apiHost={config.apiHost}
        userEmail={user.email}
        children={childrenItems}
        onCreateSubpage={openCreateSubpagePrompt}
        onNavigateToDocument={onNavigateToDocument}
      />
    </EditDocumentProvider>
  );
}

export function ViewDocument({ document, path, refetch }: ViewDocumentProps) {
  return (
    <ViewDocumentContent document={document} path={path} refetch={refetch} />
  );
}
